import { useEffect, useState, useRef } from "react";
import { Search, PackagePlus, Trash2 } from "lucide-react";
import api from "../services/api";
import Swal from "sweetalert2";

export default function InventoryLoad() {
  // ── Buscador de productos ──
  const [searchProducto, setSearchProducto] = useState("");
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);

  // ── Lista temporal de productos a cargar ──
  const [lista, setLista] = useState([]); // [{ product_id, nombre, codigo, stock_actual, cantidad }]
  const [guardando, setGuardando] = useState(false);

  const searchRef = useRef(null);

  // ── Buscar productos ──
  const buscarProductos = async (term) => {
    if (!term || term.length < 2) {
      setProductos([]);
      return;
    }
    setLoadingProductos(true);
    try {
      const res = await api.get(`/products/paginated?search=${term}&page=1`);
      setProductos(res.data.data || []);
    } catch (error) {
      console.error("Error buscando productos:", error);
    } finally {
      setLoadingProductos(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => buscarProductos(searchProducto), 400);
    return () => clearTimeout(timeout);
  }, [searchProducto]);

  // ── Agregar producto a la lista temporal ──
  const agregarProducto = (producto) => {
    setLista((prev) => {
      // Si ya está en la lista, no lo duplica, solo enfoca su fila
      const yaExiste = prev.some((p) => p.product_id === producto.id);
      if (yaExiste) return prev;

      return [
  {
    product_id: producto.id,
    nombre: producto.nombre,
    codigo: producto.codigo || "N/A",
    stock_actual: producto.stock,
    cantidad: "",
  },
  ...prev,
];
    });
    setSearchProducto("");
    setProductos([]);
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  // ── Quitar producto de la lista ──
  const quitarProducto = (product_id) => {
    setLista((prev) => prev.filter((p) => p.product_id !== product_id));
  };

  // ── Cambiar cantidad de una fila ──
  const cambiarCantidad = (product_id, valor) => {
    setLista((prev) =>
      prev.map((p) =>
        p.product_id === product_id ? { ...p, cantidad: valor } : p
      )
    );
  };

  // ── Limpiar todo ──
  const limpiarLista = () => {
    setLista([]);
    setSearchProducto("");
    setProductos([]);
  };

  // ── Guardar carga ──
  const handleGuardar = async () => {
    if (lista.length === 0) {
      Swal.fire("Atención", "Agrega al menos un producto a la lista", "warning");
      return;
    }

    const invalidos = lista.filter((p) => !p.cantidad || parseInt(p.cantidad) <= 0);
    if (invalidos.length > 0) {
      Swal.fire(
        "Atención",
        `Ingresa una cantidad válida para: ${invalidos.map((p) => p.nombre).join(", ")}`,
        "warning"
      );
      return;
    }

    const confirmacion = await Swal.fire({
      title: "¿Confirmar carga de inventario?",
      html: `
        <div style="text-align:left; padding: 8px 0;">
          ${lista
            .map(
              (p) => `
            <p style="margin:4px 0;">
              <strong>${p.nombre}</strong>: +${p.cantidad} unidades
            </p>`
            )
            .join("")}
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#38a169",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, cargar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) return;

    setGuardando(true);
    try {
      await api.post("/inventory-loads", {
        items: lista.map((p) => ({
          product_id: p.product_id,
          cantidad: parseInt(p.cantidad),
        })),
      });

      Swal.fire("✅ Registrado", "La carga de inventario fue registrada correctamente", "success");
      limpiarLista();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Error al registrar la carga", "error");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <PackagePlus size={28} color="#38a169" />
            <h1 className="card-title">Carga de Inventario</h1>
          </div>
        </div>

        <div className="card-body">
          {/* Buscar producto */}
          <div className="form-group" style={{ position: "relative", maxWidth: 500 }}>
            <label className="form-label">Producto *</label>
            <div style={{ position: "relative" }}>
              <Search
                size={16}
                style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#a0aec0" }}
              />
              <input
                ref={searchRef}
                type="text"
                className="form-control"
                placeholder="Buscar producto por nombre, código o escáner..."
                value={searchProducto}
                onChange={(e) => setSearchProducto(e.target.value)}
                style={{ paddingLeft: 36 }}
              />
            </div>

            {/* Dropdown resultados */}
            {productos.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  zIndex: 100,
                  width: "100%",
                  background: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {loadingProductos ? (
                  <div style={{ padding: 16, textAlign: "center", color: "#718096" }}>Buscando...</div>
                ) : (
                  productos.map((p) => {
                    const yaAgregado = lista.some((l) => l.product_id === p.id);
                    return (
                      <div
                        key={p.id}
                        onClick={() => !yaAgregado && agregarProducto(p)}
                        style={{
                          padding: "10px 16px",
                          cursor: yaAgregado ? "not-allowed" : "pointer",
                          opacity: yaAgregado ? 0.5 : 1,
                          borderBottom: "1px solid #f7fafc",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) => !yaAgregado && (e.currentTarget.style.background = "#f7fafc")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: "#2d3748", fontSize: 14 }}>{p.nombre}</div>
                          <div style={{ fontSize: 12, color: "#718096" }}>Código: {p.codigo || "N/A"}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {yaAgregado && (
                            <div style={{ fontWeight: 700, color: "#a0aec0", fontSize: 13 }}>
                              Ya en la lista
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* ── Lista temporal ── */}
          <div style={{ marginTop: 24 }}>
            {lista.length === 0 ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "#a0aec0", border: "2px dashed #e2e8f0", borderRadius: 10 }}>
                Aún no has agregado productos. Busca un producto arriba para comenzar.
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ textAlign: "center", width: 160 }}>Stock a ingresar *</th>
                      <th style={{ textAlign: "center", width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map((p) => (
                      <tr key={p.product_id}>
                        <td>
                          <div style={{ fontWeight: 600, color: "#2d3748" }}>{p.nombre}</div>
                          <div style={{ fontSize: 12, color: "#718096" }}>{p.codigo}</div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Ej: 10"
                            min="1"
                            value={p.cantidad}
                            onChange={(e) => cambiarCantidad(p.product_id, e.target.value)}
                            style={{ textAlign: "center" }}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            onClick={() => quitarProducto(p.product_id)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#e53e3e" }}
                            title="Quitar de la lista"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button className="btn btn-secondary" onClick={limpiarLista} disabled={lista.length === 0}>
              Limpiar
            </button>
            <button
              className="btn btn-success"
              onClick={handleGuardar}
              disabled={guardando || lista.length === 0}
              style={{ background: "#38a169", borderColor: "#38a169", color: "white" }}
            >
              {guardando ? "Guardando..." : `⬆️ Guardar Carga (${lista.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}